import { createRawRichTextContentStateWithText, IAccount, INote, IOperationResultNoValue } from '@ViewModels';
import { StyleSheet } from 'aphrodite';
import { useState } from 'react';
import { toast } from 'sonner';
import { InternalMeetingNoteType } from '../../../models/AdminModels';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import {
	AccountDetailsNoteViewModel,
	AccountOperationsViewModel,
	AdminPermission,
} from '../../../viewmodels/AdminViewModels';
import { Button } from '../../../web/components-v2/Button';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { NotesIcon } from '../../../web/components/svgs/icons/NotesIcon';
import { AccountDetailsNoteComposerModal } from '../../components/accountDetails/AccountDetailsNoteComposer';
import { ConditionalAccess } from '../../components/ConditionalAccess';
import { styleSheet as accountDetailsStyleSheet } from '../../containers/account/AccountDetails/styles';

/** Flattens the components-v2 Button's fixed sizing so it sits inline, matching this bar's other (legacy) action buttons. */
const s = StyleSheet.create({
	button: {
		background: 'none',
		border: 'none',
		boxShadow: 'none',
		height: 'auto',
		padding: 0,
		// The Button `link` variant underlines and recolors on focus, which otherwise stays applied
		// (blue) after a click even once the mouse is no longer over it — the sibling action buttons
		// have no such persistent focus color, only a hover one. ':focus' is declared before ':hover'
		// so hovering a focused button (e.g. right after clicking it) still resolves to the hover
		// color: equal-specificity pseudo-classes resolve by source order, and the later rule wins.
		':focus': {
			color: THEME.colors.text.light,
			textDecoration: 'none',
		},
		':hover': {
			color: THEME.colors.primary.main,
			textDecoration: 'none',
		},
	},
});

export interface IAddNoteQuickActionProps {
	accountOperations: AccountOperationsViewModel;
	onAccountUpdated: (account: IAccount) => void;
	/** Called after a note is actually saved (not on cancel) — the Overview tab owns its own
	 * `AccountOperationsViewModel` instance and note state, so this header action can't refresh
	 * it directly; the caller uses this to force that subtree to remount and refetch. */
	onNoteSaved: () => void;
}

/**
 * Page-header "Add a Note" quick action, gated by `AnnotateAccount` like the equivalent action
 * inside `AccountDetailsNote`'s CSM card. Lives in the header action bar (visible on every tab)
 * so leaving a CSM note doesn't require navigating to Overview and scrolling down to it. Mirrors
 * `AccountDetailsNote`'s `showNoteComposer(false)` for the CSM note: the composer opens blank but
 * keyed to the account's existing `csmNoteId`, so saving replaces the current "latest CSM note".
 */
export const AddNoteQuickAction = ({ accountOperations, onAccountUpdated, onNoteSaved }: IAddNoteQuickActionProps) => {
	const errorMessages = useErrorMessages();
	const [pendingNote, setPendingNote] = useState<AccountDetailsNoteViewModel | null>(null);

	const onAddNoteClicked = () => {
		setPendingNote(
			new AccountDetailsNoteViewModel(accountOperations, InternalMeetingNoteType.CSM, {
				content: createRawRichTextContentStateWithText(''),
				id: accountOperations.account.additionalInfo?.csmNoteId,
			})
		);
	};

	const onNoteComposerRequestClose = (note?: INote, cancel?: boolean) => {
		setPendingNote(null);
		if (!cancel && !!note?.id) {
			onNoteSaved();
		}
	};

	const onNextMeetingSelected = (nextMeeting: Date) => {
		accountOperations.updateNextMeeting(nextMeeting)?.catch((error: IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
			toast.error(error?.systemMessage || 'Failed to update next meeting');
		});
	};

	return (
		<ConditionalAccess permission={AdminPermission.AnnotateAccount}>
			<Button variant='link' styles={[accountDetailsStyleSheet.actionEntities, s.button]} onClick={onAddNoteClicked}>
				<NotesIcon fillColor={THEME.colors.success.main} />
				<span>Add Note</span>
			</Button>
			{!!pendingNote && (
				<AccountDetailsNoteComposerModal
					account={accountOperations.account}
					modalProps={{
						isOpen: !!pendingNote,
						onRequestClose: onNoteComposerRequestClose,
					}}
					note={pendingNote}
					onAccountUpdated={onAccountUpdated}
					onNextMeetingSelected={onNextMeetingSelected}
					type={InternalMeetingNoteType.CSM}
				/>
			)}
		</ConditionalAccess>
	);
};
